// ============================================================================
// MacchineBox.jsx  —  FIX_MACCHINE_IN_SICUREZZA_v1
//
// Componente autonomo per gestire le Macchine, attrezzature e impianti
// direttamente dentro la pagina "Sicurezza Azienda" (sezione 7), senza dover
// passare da Impostazioni.
//
// Usa la STESSA fonte dati del form in Impostazioni:
//   Firestore: impostazioni/macchine_attrezzature  (campo "voci")
// e gli STESSI endpoint backend gia' esistenti:
//   POST   /api/macchine/upload
//   DELETE /api/macchine/file
//   GET    /api/macchine/file
//   DELETE /api/macchine/cartella
//
// E' un componente isolato: contiene i propri stati e le proprie funzioni.
// Non tocca Gestionale.jsx ne' il resto di SicurezzaAzienda.jsx.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, onSnapshot, setDoc } from './firebase-shim';

// Theme allineato a quello di SicurezzaAzienda.jsx
const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#22263a",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  info: "#378ADD",
  warning: "#D4537E",
  error: "#f06060",
};

const inputSt = {
  background: "#0f1117",
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "'DM Sans',sans-serif",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

export default function MacchineBox() {
  const [macchineAttr, setMacchineAttr] = useState([]);
  const [nuovaMacchinaNome, setNuovaMacchinaNome] = useState("");
  const [nuovaMacchinaLibretto, setNuovaMacchinaLibretto] = useState(false);
  const [nuovaMacchinaCollaudo, setNuovaMacchinaCollaudo] = useState(false);
  const [nuovaMacchinaVerifiche, setNuovaMacchinaVerifiche] = useState(false);
  const [nuovaMacchinaUsoComune, setNuovaMacchinaUsoComune] = useState(false);
  const [editingMacchinaId, setEditingMacchinaId] = useState(null);

  // Carica le macchine da Firestore (sync continuo, come in Impostazioni)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "impostazioni", "macchine_attrezzature"), snap => {
      const data = snap.data();
      setMacchineAttr(Array.isArray(data?.voci) ? data.voci : []);
    });
    return () => { try { unsub && unsub(); } catch(e){} };
  }, []);

  const salvaMacchine = async (lista) => {
    try {
      await setDoc(doc(db, "impostazioni", "macchine_attrezzature"), { voci: lista, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ " + e.message); }
  };

  const uploadDocMacchina = async (macchinaId, tipo, file) => {
    try {
      const token = localStorage.getItem('bertoc_jwt');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('macchinaId', macchinaId);
      fd.append('tipo', tipo);
      const r = await fetch('/api/macchine/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ('HTTP ' + r.status));
      }
      const data = await r.json();
      const lista = macchineAttr.map(m => {
        if (m.id !== macchinaId) return m;
        const docs = m.documenti || {};
        const arr = Array.isArray(docs[tipo]) ? docs[tipo] : [];
        if (arr.includes(data.filename)) return m;
        return { ...m, documenti: { ...docs, [tipo]: [...arr, data.filename] } };
      });
      await salvaMacchine(lista);
      return true;
    } catch (e) {
      alert("❌ Upload fallito: " + e.message);
      return false;
    }
  };

  const deleteDocMacchina = async (macchinaId, tipo, filename) => {
    if (!window.confirm(`Eliminare "${filename}"?`)) return;
    try {
      const token = localStorage.getItem('bertoc_jwt');
      const url = '/api/macchine/file?macchinaId=' + encodeURIComponent(macchinaId)
                + '&tipo=' + encodeURIComponent(tipo)
                + '&filename=' + encodeURIComponent(filename);
      const r = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token },
      });
      if (!r.ok && r.status !== 404) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ('HTTP ' + r.status));
      }
      const lista = macchineAttr.map(m => {
        if (m.id !== macchinaId) return m;
        const docs = m.documenti || {};
        const arr = (Array.isArray(docs[tipo]) ? docs[tipo] : []).filter(f => f !== filename);
        return { ...m, documenti: { ...docs, [tipo]: arr } };
      });
      await salvaMacchine(lista);
    } catch (e) {
      alert("❌ " + e.message);
    }
  };

  const resetForm = () => {
    setEditingMacchinaId(null);
    setNuovaMacchinaNome("");
    setNuovaMacchinaLibretto(false);
    setNuovaMacchinaCollaudo(false);
    setNuovaMacchinaVerifiche(false);
    setNuovaMacchinaUsoComune(false);
  };

  return (
    <div style={{ maxWidth:900 }}>
      <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
        Configura le macchine, attrezzature e impianti di Bertoc Project. Le voci appariranno nel modal "Genera POS" per essere flaggate quando presenti in un cantiere specifico.
      </div>

      {/* Form aggiungi nuova */}
      <div style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, padding:14, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>
          {editingMacchinaId ? "✎ Modifica attrezzatura" : "+ Aggiungi attrezzatura"}
        </div>
        <input style={{...inputSt, marginBottom:10}}
          placeholder="es. Molatrice portatile, Avvitatore elettrico, Trabattello..."
          value={nuovaMacchinaNome}
          onChange={e=>setNuovaMacchinaNome(e.target.value)}
        />
        <div style={{ display:"flex", flexWrap:"wrap", gap:14, marginBottom:12 }}>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
            <input type="checkbox" checked={nuovaMacchinaLibretto} onChange={e=>setNuovaMacchinaLibretto(e.target.checked)} />
            Libretto disponibile
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
            <input type="checkbox" checked={nuovaMacchinaCollaudo} onChange={e=>setNuovaMacchinaCollaudo(e.target.checked)} />
            Soggetto a collaudo
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
            <input type="checkbox" checked={nuovaMacchinaVerifiche} onChange={e=>setNuovaMacchinaVerifiche(e.target.checked)} />
            Verifiche periodiche
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
            <input type="checkbox" checked={nuovaMacchinaUsoComune} onChange={e=>setNuovaMacchinaUsoComune(e.target.checked)} />
            Uso comune con altre imprese
          </label>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn btn-primary btn-sm" onClick={()=>{
            if (!nuovaMacchinaNome.trim()) return;
            const trimmed = nuovaMacchinaNome.trim();
            if (editingMacchinaId) {
              const updated = macchineAttr.map(m => m.id === editingMacchinaId
                ? { ...m, nome: trimmed, libretto: nuovaMacchinaLibretto, collaudo: nuovaMacchinaCollaudo, verifiche: nuovaMacchinaVerifiche, usoComune: nuovaMacchinaUsoComune }
                : m
              );
              salvaMacchine(updated);
              setEditingMacchinaId(null);
            } else {
              const exists = macchineAttr.some(m => m.nome.toLowerCase() === trimmed.toLowerCase());
              if (exists) { alert("Attrezzatura già esistente!"); return; }
              const newId = "macchina_" + Date.now() + "_" + Math.random().toString(36).slice(2, 7);
              const ordine = macchineAttr.length + 1;
              const updated = [...macchineAttr, { id: newId, nome: trimmed, libretto: nuovaMacchinaLibretto, collaudo: nuovaMacchinaCollaudo, verifiche: nuovaMacchinaVerifiche, usoComune: nuovaMacchinaUsoComune, ordine }];
              salvaMacchine(updated);
            }
            setNuovaMacchinaNome("");
            setNuovaMacchinaLibretto(false);
            setNuovaMacchinaCollaudo(false);
            setNuovaMacchinaVerifiche(false);
            setNuovaMacchinaUsoComune(false);
          }}>{editingMacchinaId ? "💾 Salva modifiche" : "+ Aggiungi"}</button>
          {editingMacchinaId && (
            <button className="btn btn-ghost btn-sm" onClick={resetForm}>Annulla</button>
          )}
        </div>

        {/* sezione documenti (solo in modalita' modifica) */}
        {editingMacchinaId && (() => {
          const m = macchineAttr.find(x => x.id === editingMacchinaId);
          if (!m) return null;
          const docs = m.documenti || {};
          const sezioni = [
            { tipo: 'libretto',  label: '📘 Libretto' },
            { tipo: 'collaudo',  label: '✅ Collaudo' },
            { tipo: 'verifiche', label: '🔄 Verifiche periodiche' },
          ];
          return (
            <div style={{ marginTop:16, paddingTop:14, borderTop:`1px dashed ${theme.border}` }}>
              <div style={{ fontSize:12, fontWeight:600, color:theme.textMuted, marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>
                📎 Documenti caricati
              </div>
              {sezioni.map(s => {
                const files = Array.isArray(docs[s.tipo]) ? docs[s.tipo] : [];
                return (
                  <div key={s.tipo} style={{ marginBottom:14, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:theme.text }}>{s.label}</div>
                    {files.length === 0 ? (
                      <div style={{ fontSize:12, color:theme.textDim, fontStyle:"italic", marginBottom:8 }}>(nessun documento)</div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:8 }}>
                        {files.map(fn => (
                          <div key={fn} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:theme.text }}>
                            <span style={{ flex:1 }}>📄 {fn}</span>
                            <a href={`/api/macchine/file?macchinaId=${encodeURIComponent(m.id)}&tipo=${s.tipo}&filename=${encodeURIComponent(fn)}`}
                              target="_blank" rel="noreferrer"
                              style={{ color:theme.accent, fontSize:11, textDecoration:"none" }}
                              onClick={async (e) => {
                                e.preventDefault();
                                const token = localStorage.getItem('bertoc_jwt');
                                const r = await fetch(e.currentTarget.href, { headers: { 'Authorization': 'Bearer ' + token } });
                                if (!r.ok) { alert("Errore caricamento file"); return; }
                                const blob = await r.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                              }}>👁️ Apri</a>
                            <button onClick={() => deleteDocMacchina(m.id, s.tipo, fn)}
                              style={{ background:"none", border:"none", color:theme.error, cursor:"pointer", fontSize:13 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label style={{
                      display:"inline-block", padding:"6px 12px", borderRadius:6,
                      background:theme.surface, border:`1px solid ${theme.border}`,
                      cursor:"pointer", fontSize:12, color:theme.textMuted,
                    }}>
                      + Carica PDF
                      <input type="file" accept="application/pdf" style={{ display:"none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 10 * 1024 * 1024) { alert("File troppo grande (max 10MB)"); return; }
                          await uploadDocMacchina(m.id, s.tipo, file);
                          e.target.value = "";
                        }} />
                    </label>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Lista attrezzature */}
      <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:theme.textMuted }}>
        📋 Elenco attuale ({macchineAttr.length} {macchineAttr.length === 1 ? "voce" : "voci"})
      </div>
      {macchineAttr.length === 0 ? (
        <div style={{ textAlign:"center", padding:"24px 0", color:theme.textDim, fontSize:13 }}>
          Nessuna attrezzatura. Aggiungine una per iniziare.
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {macchineAttr.slice().sort((a,b) => (a.ordine||0) - (b.ordine||0)).map(m => (
            <div key={m.id} style={{
              display:"flex", alignItems:"center", gap:10, padding:"8px 12px",
              background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8,
            }}>
              <div style={{ flex:1, fontSize:13, color:theme.text, fontWeight:500 }}>🔧 {m.nome}</div>
              <div style={{ display:"flex", gap:8, fontSize:11, color:theme.textMuted }}>
                {m.libretto && <span title="Libretto">📘</span>}
                {m.collaudo && <span title="Collaudo">✅</span>}
                {m.verifiche && <span title="Verifiche periodiche">🔄</span>}
                {m.usoComune && <span title="Uso comune">👥</span>}
              </div>
              <button onClick={()=>{
                setEditingMacchinaId(m.id);
                setNuovaMacchinaNome(m.nome);
                setNuovaMacchinaLibretto(!!m.libretto);
                setNuovaMacchinaCollaudo(!!m.collaudo);
                setNuovaMacchinaVerifiche(!!m.verifiche);
                setNuovaMacchinaUsoComune(!!m.usoComune);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} style={{ background:"none", border:"none", color:theme.accent, cursor:"pointer", fontSize:14 }}>✎</button>
              <button onClick={async ()=>{
                if (!window.confirm(`Eliminare "${m.nome}"?\n\nI documenti caricati saranno spostati nel cestino del NAS.`)) return;
                try {
                  const token = localStorage.getItem('bertoc_jwt');
                  const r = await fetch('/api/macchine/cartella?macchinaId=' + encodeURIComponent(m.id), {
                    method: 'DELETE',
                    headers: { 'Authorization': 'Bearer ' + token },
                  });
                  if (!r.ok && r.status !== 404) {
                    console.warn('[MACCHINE] Cestino HTTP', r.status, '- procedo comunque');
                  }
                } catch (e) {
                  console.warn('[MACCHINE] Errore cestino:', e.message);
                }
                salvaMacchine(macchineAttr.filter(x=>x.id !== m.id));
              }} style={{ background:"none", border:"none", color:theme.error, cursor:"pointer", fontSize:14 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

